import { createApp, nextTick } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import _ from "lodash";
import icons from "@/icons"; // This import may appear unused, but is necessary for FA icons to show
import config from "@/config";
import { i18n, initLocaleFromStore } from "@/i18n";
import { router, applyRouteGuards } from "@/router";

// ////////////////////////// The main App ///////////////////////////////////////////////
import App from "@/App.vue";

const app = createApp(App);
const pinia = createPinia();
if (window.playwright) {
  window.piniaStore = pinia;
}
pinia.use(piniaPluginPersistedstate);
app.use(pinia);

// ////////////////////////// Vue ////////////////////////////////////////////////////////
app.config.globalProperties.productionTip = false;
// Silence warnings by uncommenting the following line
app.config.warnHandler = function (msg, vm, trace) {};

// Vite migration note:
// This globalProperties below still works with vite but we'd have to rename everything from _. to this.$lodash.
// for now there's AutoImport in vite.config.js to import lodash in every vue component
// but preferraebly we'd just import only really necessary lodash things separately per component
Object.defineProperty(app.config.globalProperties, "$lodash", { value: _ });

// Add explicit error handler to ensure errors are logged in vue composition api
app.config.errorHandler = function (err, vm, info) {
  if (import.meta.env.MODE === "development") {
    console.error("Error:", err);
    console.error("Error info:", info);
  }
};

// ////////////////////////// Dynamic meta tags in <head> ////////////////////////////////
import { createHead } from "@unhead/vue/client";
const head = createHead();
app.use(head);

// ////////////////////////// Matomo analytics ///////////////////////////////////////////
import VueMatomo from "vue-matomo";
if (config.matomo && process.env.VITE_NODE_ENV !== "development") {
  app.use(VueMatomo, {
    host: "https://analytics.maptionnaire.com",
    router: router,
    siteId: config.matomo.siteId,
    domains: config.matomo.domains,
    // Use no-cookie mode
    disableCookies: true,
  });
}

// ////////////////////////// Sentry error tracking //////////////////////////////////////
import * as Sentry from "@sentry/vue";

if (config.sentry) {
  Sentry.init({
    app,
    dsn: config.sentry.dsn,
    release: import.meta.env.VITE_SENTRY_RELEASE || "development",
    ignoreErrors: [
      // These errors are likely caused by a non standard web crawler and compounded by
      // a bug in sentry which goes into a loop and triggers hundreds of these
      // See: https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062
      "Non-Error exception captured",
      "Non-Error promise rejection captured",
      // visualViewport API not available in some in-app browsers (Facebook, Instagram, etc.),
      // old WebViews, and web crawlers. Error comes from third-party libraries (likely Vuetify
      // or Floating UI) that don't feature-detect before accessing window.visualViewport.
      "Can't find variable: visualViewport",
      // Dynamic import failures - usually caused by user navigating away or closing the tab
      // before lazy-loaded chunks finish downloading. Not actionable.
      "Failed to fetch dynamically imported module",
      // CSS preload failures - same cause as above, user left before CSS finished loading
      "Unable to preload CSS",
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Filter out expected HTTP errors that aren't actual bugs
      if (error && error.isAxiosError) {
        const status = error.response?.status;
        const url = error.config?.url || "";

        // 401 Unauthorized - expected when user is not logged in or session expired
        if (status === 401) {
          return null;
        }

        // Network errors during logout - expected when user closes tab or loses connection
        if (!error.response && url.includes("/auth/logout")) {
          return null;
        }

        // 404 on Azure/SCIM config - expected when SSO is not configured for a group
        if (status === 404 && url.includes("/azure/config")) {
          return null;
        }

        // 400 on SCIM config - expected when SCIM is not configured
        if (status === 400 && url.includes("/scim/config")) {
          return null;
        }
      }

      // Filter out console.error captures that contain expected Axios errors
      // (captureConsoleIntegration wraps these differently from unhandled exceptions)
      if (event.logger === "console" && event.message) {
        if (event.message.includes("status code 401")) {
          return null;
        }
        if (event.message.includes("Network Error") && event.message.includes("logout")) {
          return null;
        }
      }

      return event;
    },
    logErrors: import.meta.env.MODE === "development" ? true : false,
    trackComponents: true,
    integrations: [
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
      Sentry.browserTracingIntegration({ router }),
    ],
    tracePropagationTargets: ["localhost", "us.maptionnaire.com", "app.maptionnaire.com", /^\//],
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 1.0,
  });
}
// ////////////////////////// Vue media queries //////////////////////////////////////////
// NOTE: these should match the ones set in /styles/dimensions.scss
import { Vue3Mq } from "vue3-mq";

app.use(Vue3Mq, {
  breakpoints: {
    xs: 0,
    sm: 480,
    md: 900,
    lg: 1250,
    xl: 1450,
  },
});

// Vuetify
import "@/styles/vuetify-main.scss"; // replaces import "vuetify/styles";
import { createVuetify } from "vuetify";
import { mptTheme, vantaaTheme, defaults } from "./plugins/vuetify";
import { VAlert } from "vuetify/components/VAlert";
import { VContainer, VRow, VCol, VSpacer } from "vuetify/components/VGrid";
import { VBadge } from "vuetify/components/VBadge";
import { VBtn } from "vuetify/components/VBtn";
import { VBtnToggle } from "vuetify/components/VBtnToggle";
import { VBtnGroup } from "vuetify/components/VBtnGroup";
import { VHover } from "vuetify/components/VHover";
import { VTextField } from "vuetify/components/VTextField";
import { VTextarea } from "vuetify/components/VTextarea";
import { VSelect } from "vuetify/components/VSelect";
import { VAutocomplete } from "vuetify/components/VAutocomplete";
import { VNumberInput } from "vuetify/components/VNumberInput";
import { VSlider } from "vuetify/components/VSlider";
import { VRangeSlider } from "vuetify/components/VRangeSlider";
import { VMenu } from "vuetify/components/VMenu";
import {
  VList,
  VListItem,
  VListItemTitle,
  VListGroup,
  VListSubheader,
} from "vuetify/components/VList";
import { VCombobox } from "vuetify/components/VCombobox";
import { VSwitch } from "vuetify/components/VSwitch";
import { VDialog } from "vuetify/components/VDialog";
import { VCard, VCardActions, VCardTitle, VCardText } from "vuetify/components/VCard";
import { VRadioGroup } from "vuetify/components/VRadioGroup";
import { VDateInput } from "vuetify/labs/VDateInput";
import { VTimePicker } from "vuetify/components/VTimePicker";
import { VRadio } from "vuetify/components/VRadio";
import { VCheckbox, VCheckboxBtn } from "vuetify/components/VCheckbox";
import { VTable } from "vuetify/components/VTable";
import { VSheet } from "vuetify/components/VSheet";
import { VChip } from "vuetify/components/VChip";
import { VTooltip } from "vuetify/components/VTooltip";
import { VEmptyState } from "vuetify/components/VEmptyState";
import { VToolbar, VToolbarTitle } from "vuetify/components/VToolbar";
import {
  VExpansionPanel,
  VExpansionPanelText,
  VExpansionPanels,
  VExpansionPanelTitle,
} from "vuetify/components/VExpansionPanel";
import { VImg } from "vuetify/components/VImg";
import {
  VDataTable,
  VDataTableServer,
  VDataTableFooter,
  VDataTableHeaders,
  VDataTableRow,
  VDataTableRows,
} from "vuetify/components/VDataTable";
import { VTabs, VTab, VTabsWindow, VTabsWindowItem } from "vuetify/components/VTabs";
import { VSnackbar } from "vuetify/components/VSnackbar";
import { VProgressCircular } from "vuetify/components/VProgressCircular";
import { VProgressLinear } from "vuetify/components/VProgressLinear";
import { VIcon } from "vuetify/components/VIcon";
import { VDivider } from "vuetify/components/VDivider";
import { VOtpInput } from "vuetify/components/VOtpInput";
import { VSkeletonLoader } from "vuetify/components/VSkeletonLoader";
import { VStepperVertical, VStepperVerticalItem } from "vuetify/labs/VStepperVertical";
import { mdi } from "vuetify/iconsets/mdi";

const vuetify = createVuetify({
  // only include the components we use
  components: {
    VAlert,
    VContainer,
    VRow,
    VCol,
    VBadge,
    VBtn,
    VBtnToggle,
    VBtnGroup,
    VHover,
    VTextField,
    VTextarea,
    VSelect,
    VAutocomplete,
    VNumberInput,
    VSlider,
    VRangeSlider,
    VMenu,
    VDateInput,
    VTimePicker,
    VList,
    VListItem,
    VListItemTitle,
    VListGroup,
    VListSubheader,
    VCombobox,
    VSwitch,
    VDialog,
    VCard,
    VCardActions,
    VCardTitle,
    VCardText,
    VRadio,
    VRadioGroup,
    VCheckbox,
    VCheckboxBtn,
    VTable,
    VSheet,
    VChip,
    VEmptyState,
    VExpansionPanel,
    VExpansionPanelText,
    VExpansionPanels,
    VExpansionPanelTitle,
    VImg,
    VDataTable,
    VDataTableServer,
    VDataTableFooter,
    VDataTableHeaders,
    VDataTableRow,
    VDataTableRows,
    VTabs,
    VTab,
    VTabsWindow,
    VTabsWindowItem,
    VTooltip,
    VSnackbar,
    VToolbar,
    VToolbarTitle,
    VSpacer,
    VProgressCircular,
    VProgressLinear,
    VIcon,
    VDivider,
    VOtpInput,
    VSkeletonLoader,
    VStepperVertical,
    VStepperVerticalItem,
  },
  theme: {
    defaultTheme: "mptTheme",
    themes: {
      mptTheme,
      vantaaTheme,
    },
  },
  aliases: {
    VBtnCta: VBtn,
    VBtnMenu: VBtn,
    VCardTitleWrap: VCardTitle,
    VTabsBoxed: VTabs,
  },
  defaults,
  icons: {
    defaultSet: "mdi",
    sets: {
      mdi,
    },
  },
});

app.use(vuetify);

// ////////////////////////// Oruga (to replace Buefy) ///////////////////////////////////
import Oruga from "@oruga-ui/oruga-next";
import { bulmaConfig } from "@oruga-ui/theme-bulma";

app.use(Oruga, {
  ...bulmaConfig,
  iconPack: "fal",
});

// This mixin can be used to check how many times method/computed has been called in vue components.
// app.config.devtools is only present in dev environment
if (app.config.devtools) window.__mpt_data__ = {};
app.mixin({
  methods: {
    $instrument: function (location) {
      if (!app?.config?.devtools) return;
      if (!window.__mpt_data__[location]) window.__mpt_data__[location] = 1;
      else window.__mpt_data__[location]++;
    },
    $getInstrumentationData: function () {
      // eslint-disable-next-line no-console
      if (app.config.devtools) console.log("DATA", window.__mpt_data__);
    },
  },
});

function setSelectedInputButton(el, binding, vnode) {
  // If the radio is checked, set the colour of the button to primary and
  // add is-selected class which buefy is using
  if (el?.getElementsByTagName("input")?.[0].checked) {
    el.classList.add("is-primary", "is-selected");
  } else {
    el.classList.remove("is-primary", "is-selected");
  }
}

// Directive for making Oruga o-checkbox like Buefy's o-checkbox-button.
// Use like: `<o-radio v-checkbox-button>...
app.directive("checkbox-button", {
  beforeMount: function (el, binding, vnode) {
    el.classList.add("button");
    // Oruga adds an extra span with "control-label" around the label with padding,
    // but we don't want that for the buttons
    el.getElementsByClassName("control-label")?.[0].classList.remove("control-label");
    const checkboxInput = el.querySelectorAll("input[type=checkbox]")?.[0];
    checkboxInput.classList.add("is-hidden");
  },
  updated: function (el, binding, vnode) {
    nextTick(function () {
      // for unknown reasons button class is removed when a checkbox
      // becomes disabled, so we have to add it again
      if (!el.classList.contains("button")) {
        el.classList.add("button");
      }
    });
  },
});

// Icons?
import {
  FontAwesomeIcon,
  FontAwesomeLayers,
  FontAwesomeLayersText,
} from "@fortawesome/vue-fontawesome";

// register FA components
app.component("fa-icon", FontAwesomeIcon);
app.component("fa-layers", FontAwesomeLayers);
app.component("fa-layers-text", FontAwesomeLayersText);

// Install instances to make the whole app aware
applyRouteGuards();
app.use(router);
initLocaleFromStore();
app.use(i18n);

// Mount app
router.isReady().then(() => {
  app.mount("#app");
});
